import React, { useEffect, useRef } from 'react';
import { useTheme } from '@principal-ade/industry-theme';
import type { CommandPaletteProps } from '../types/command.types';
import { CommandInput } from './CommandInput';
import { CommandList } from './CommandList';

/**
 * CommandPalette - Vim-style command palette component
 * Displays at the bottom of the screen with an input field and command list
 */
export const CommandPalette: React.FC<CommandPaletteProps> = ({
  commandPalette,
  config,
}) => {
  const { theme } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    isOpen,
    close,
    query,
    setQuery,
    filteredCommands,
    selectedIndex,
    setSelectedIndex,
    executeSelected,
  } = commandPalette;

  // Handle click outside to close
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        close();
      }
    };

    // Add a small delay to prevent immediate close on open
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, close]);


  if (!isOpen) {
    return null;
  }

  const maxHeight = config?.maxHeight || '50vh';
  const placeholder = config?.placeholder || '> Type a command...';

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(2px)',
          zIndex: 999,
        }}
        onClick={close}
      />

      {/* Command Palette Container */}
      <div
        ref={containerRef}
        className={config?.className}
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          maxHeight,
          backgroundColor: theme.colors.surface,
          borderTop: `1px solid ${theme.colors.border}`,
          boxShadow: theme.shadows[2],
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column-reverse',
          ...config?.style,
        }}
      >
        {/* Input Field (at bottom due to column-reverse) */}
        <div>
          <CommandInput
            value={query}
            onChange={setQuery}
            placeholder={placeholder}
            onClose={close}
            onEnter={executeSelected}
          />

          {/* Footer with keyboard hints */}
          <div
            style={{
              padding: '8px 16px',
              borderTop: `1px solid ${theme.colors.border}`,
              display: 'flex',
              gap: '16px',
              fontSize: '12px',
              color: theme.colors.textMuted,
              backgroundColor: theme.colors.background,
            }}
          >
            <span>
              <kbd style={{ padding: '2px 6px', backgroundColor: theme.colors.backgroundSecondary, borderRadius: '3px', border: `1px solid ${theme.colors.border}` }}>Enter</kbd> Execute
            </span>
            <span>
              <kbd style={{ padding: '2px 6px', backgroundColor: theme.colors.backgroundSecondary, borderRadius: '3px', border: `1px solid ${theme.colors.border}` }}>Esc</kbd> Close
            </span>
          </div>
        </div>

        {/* Results List (appears above due to column-reverse) */}
        {filteredCommands.length > 0 && (
          <CommandList
            commands={filteredCommands}
            selectedIndex={selectedIndex}
            onSelect={(command) => {
              commandPalette.executeCommand(command.id);
            }}
            onHover={setSelectedIndex}
            maxResults={config?.maxResults}
          />
        )}

        {/* No Results Message (appears above due to column-reverse) */}
        {query && filteredCommands.length === 0 && (
          <div
            style={{
              padding: '24px 16px',
              color: theme.colors.textMuted,
              textAlign: 'center',
              fontSize: '14px',
            }}
          >
            No commands found for "{query}"
          </div>
        )}
      </div>
    </>
  );
};
